import { useUserStore } from '@/stores/UserStore'
import { Auth, Options, CallbackQueueObject, MSALBasic } from './msalTypes'
import { AuthenticationResult, PublicClientApplication, RedirectRequest } from '@azure/msal-browser'
import { CacheOptions } from '@azure/msal-browser/dist/config/Configuration'
import { jwtDecode } from 'jwt-decode'

export class MSAL implements MSALBasic {
  private lib: PublicClientApplication
  private tokenExpirationTimers: { [key: string]: undefined | number } = {}
  public data: any = {
    isAuthenticated: false,
    accessToken: '',
    idToken: '',
    user: {},
    graph: {},
    custom: {}
  }

  public callbackQueue: CallbackQueueObject[] = []
  private readonly auth: Auth = {
    clientId: '',
    authority: '',
    validateAuthority: true,
    redirectUri: window.location.href,
    postLogoutRedirectUri: window.location.href,
    navigateToLoginRequestUrl: true,
    requireAuthOnInitialize: false,
    autoRefreshToken: true,
    onAuthentication: (error, response) => {},
    onToken: (error, response) => {},
    beforeSignOut: () => {}
  }

  private readonly cache: CacheOptions = {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true
  }

  private readonly request: RedirectRequest = {
    scopes: ['user.read']
  }

  private readonly isDisabled: boolean = false

  constructor(private readonly options: Options) {
    if (!options.auth.clientId) {
      throw new Error('auth.clientId is required')
    }
    this.auth = Object.assign(this.auth, options.auth)
    this.cache = Object.assign(this.cache, options.cache)
    this.request = Object.assign(this.request, options.request)
    this.isDisabled = options.disabled ?? false

    const input = {
      auth: {
        clientId: this.auth.clientId,
        authority: this.auth.authority,
        redirectUri: this.auth.redirectUri as string,
        postLogoutRedirectUri: this.auth.postLogoutRedirectUri as string,
        navigateToLoginRequestUrl: this.auth.navigateToLoginRequestUrl
      },
      cache: this.cache
    }

    this.lib = new PublicClientApplication(input)
  }

  async initialize() {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return
    }
    try {
      await this.lib.initialize()

      try {
        const response = await this.lib.handleRedirectPromise()
        if (response) {
          await this.handleTokenResponse(response)
        } else {
          console.log('No token found in cache. User may not be authenticated.')
        }
      } catch (error) {
        console.error('Error handling redirect promise:', error)
      }

      this.data.isAuthenticated = this.isAuthenticated()
      if (this.data.isAuthenticated) {
        this.data.user = this.getAccount()
      }
    } catch (error) {
      console.log('Initialization error', error)
      throw error
    }
  }

  async handleRedirect() {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return
    }
    await this.lib.handleRedirectPromise()
  }

  getAccount() {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return
    }

    if (this.lib.getAllAccounts().length > 0) {
      return this.lib.getAllAccounts()[0]
    } else {
      return null
    }
  }

  async login() {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return
    }

    try {
      if (!this.lib) {
        throw new Error('MSAL not initialized. Call initializeMsal() before using MSAL API.')
      }

      if (!this.getAccount()) {
        await this.lib.loginRedirect(this.request)
      }
    } catch (error) {
      console.error('Login error:', error)
    }
  }

  async logout() {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return
    }

    if (!this.lib) {
      throw new Error('MSAL not initialized. Call initializeMsal() before using MSAL API.')
    }

    this.removeTokenFromCookies()

    await this.lib.clearCache()
    await this.lib.logoutRedirect()
  }

  isAuthenticated() {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return true
    }

    if (this.isTokenExpired()) {
      this.removeTokenFromCookies()
    }

    return !!this.getAccount()
  }

  isTokenExpired() {
    const token = this.getTokenFromCookies()
    if (token) {
      const decodedToken = jwtDecode(atob(token))
      const currentTime = Math.floor(Date.now() / 1000)

      return decodedToken.exp! < currentTime
    }
    return false
  }

  async acquireToken(request = this.request) {
    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return null
    }

    try {
      const response =
        this.data.user && Object.keys(this.data.user).length > 0
          ? await this.lib.acquireTokenSilent({ ...request, account: this.data.user })
          : await this.lib.acquireTokenSilent(request)
      return response
    } catch (error: any) {
      if (this.requiresInteraction(error.errorCode)) {
        await this.handleRedirect()
        await this.lib.acquireTokenRedirect(request)
      }

      return null
    }
  }

  async userUpdates(request = this.request) {
    const silentRequest = {
      scopes: ['User.Read', 'Mail.Read']
    }

    this.removeTokenFromCookies()

    if (this.isDisabled) {
      console.debug('Msal is disabled')
      return null
    }

    try {
      const response = await this.lib.ssoSilent(silentRequest)
      return response
    } catch (error: any) {
      if (this.requiresInteraction(error.errorCode)) {
        await this.acquireToken()
        await this.lib.acquireTokenRedirect(request)
      }

      return null
    }
  }

  private handleTokenResponse(response: AuthenticationResult) {
    let setCallback = false
    if (this.data.accessToken !== response.accessToken) {
      this.setToken('accessToken', response.accessToken, response.expiresOn!, response.scopes)
      setCallback = true
    }
    if (this.data.idToken !== response.idToken) {
      this.setToken('idToken', response.idToken, response.expiresOn!, [this.auth.clientId])
      setCallback = true
    }
  }

  private setToken(tokenType: string, token: string, expiresOn: Date, scopes: string[]) {
    const expirationOffset = this.lib.getConfiguration().system.tokenRenewalOffsetSeconds * 1000
    const expiration = expiresOn.getTime() - new Date().getTime() - expirationOffset
    if (expiration >= 0) {
      this.data[tokenType] = token
    }
    if (this.tokenExpirationTimers[tokenType]) clearTimeout(this.tokenExpirationTimers[tokenType])
    this.tokenExpirationTimers[tokenType] = window.setTimeout(async () => {
      if (this.auth.autoRefreshToken) {
        await this.acquireToken({ scopes })
      } else {
        this.data[tokenType] = ''
      }
    }, expiration)
  }

  private requiresInteraction(errorCode: string) {
    if (!errorCode || !errorCode.length) {
      return false
    }
    return (
      errorCode === 'no_account_error' ||
      errorCode === 'consent_required' ||
      errorCode === 'interaction_required' ||
      errorCode === 'login_required'
    )
  }

  getTokenFromCookies(): string | null {
    const match = document.cookie.match(new RegExp('(^| )access-token=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : null
  }

  removeTokenFromCookies(): void {
    const token = this.getTokenFromCookies()
    if (token) {
      document.cookie = 'access-token=; Max-Age=0; path=/'
    }
  }
}
